import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import axios, { AxiosError } from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import GlobalLoader from '../components/ui/GlobalLoader';

interface DemoRequestFormData {
  pharmacyName: string;
  contactPersonName: string;
  contactEmail: string;
  contactPhone: string;
  note: string;
  preferredTimeToContact: string;
  preferredDate: string;
}

const timeSlots = [
  '9:00 AM - 10:00 AM',
  '10:00 AM - 11:00 AM',
  '11:00 AM - 12:00 PM',
  '12:00 PM - 1:00 PM',
  '1:00 PM - 2:00 PM',
  '2:00 PM - 3:00 PM',
  '3:00 PM - 4:00 PM',
  '4:00 PM - 5:00 PM',
  '5:00 PM - 6:00 PM',
  '6:00 PM - 7:00 PM',
  '7:00 PM - 8:00 PM'
];

export default function DemoRequestPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [formData, setFormData] = useState<DemoRequestFormData>({
    pharmacyName: '',
    contactPersonName: '',
    contactEmail: '',
    contactPhone: '',
    note: '',
    preferredTimeToContact: '',
    preferredDate: ''
  });

  const handleInputChange = (field: keyof DemoRequestFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const baseURL = import.meta.env.VITE_API_BASE_URL;
      
      const response = await axios.post(`${baseURL}/demo-requests`, formData);
      
      if (response.data.success) {
        setIsSubmitted(true);
        toast.success('Demo request submitted successfully! We will contact you soon.');
      }
    } catch (error) {
      if(axios.isAxiosError(error) && error.response?.data) {
        toast.error(error.response?.data?.message || 'Failed to submit demo request');
      } else {
        toast.error('An unexpected error occurred');
      }
      
      // if (axios.isAxiosError(error) && error.response) {
      //   toast.error(error.response.data?.message || 'Failed to submit demo request');
      // } else {
      //   toast.error('An unexpected error occurred');
      // }
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      pharmacyName: '',
      contactPersonName: '',
      contactEmail: '',
      contactPhone: '',
      note: '',
      preferredTimeToContact: '',
      preferredDate: ''
    });
    setIsSubmitted(false);
  };

  if (isSubmitted) {
    return (
      
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-gray-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="shadow-xl border-0">
            <CardContent className="pt-6">
              <div className="text-center">
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                  Demo Request Submitted!
                </h2>
                <p className="text-gray-600 mb-6">
                  Thank you for your interest in Courier-RX. We will contact you at your preferred time to schedule a demo.
                </p>
                <div className="space-y-3">
                  <Button
                    onClick={() => navigate('/')}
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                  >
                    Return to Home
                  </Button>
                  <Button
                    onClick={resetForm}
                    variant="outline"
                    className="w-full"
                  >
                    Submit Another Request
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img src="/assets/courier-logo.svg" alt="Courier-RX" className="h-12" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Book a Demo</h1>
          <p className="text-gray-600 mt-2">Schedule a personalized demo of Courier-RX platform</p>
        </div>

        <Card className="shadow-xl border-0">
          <CardHeader className="space-y-1 pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl">Demo Request Form</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Pharmacy Information */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">Pharmacy Information</h3>
                <div className="space-y-2">
                  <Label htmlFor="pharmacyName">Pharmacy Name *</Label>
                  <Input
                    id="pharmacyName"
                    placeholder="Enter your pharmacy name"
                    value={formData.pharmacyName}
                    onChange={(e) => handleInputChange('pharmacyName', e.target.value)}
                    required
                    className="h-11"
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contactPersonName">Contact Person Name *</Label>
                    <Input
                      id="contactPersonName"
                      placeholder="Enter your full name"
                      value={formData.contactPersonName}
                      onChange={(e) => handleInputChange('contactPersonName', e.target.value)}
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contactEmail">Email Address *</Label>
                    <Input
                      id="contactEmail"
                      type="email"
                      placeholder="Enter your email address"
                      value={formData.contactEmail}
                      onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                      required
                      className="h-11"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactPhone">Phone Number *</Label>
                  <Input
                    id="contactPhone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.contactPhone}
                    onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                    required
                    className="h-11"
                  />
                </div>
              </div>

              {/* Preferred Time and Date */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">Preferred Demo Schedule</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="preferredDate">Preferred Date *</Label>
                    <Input
                      id="preferredDate"
                      type="date"
                      value={formData.preferredDate}
                      onChange={(e) => handleInputChange('preferredDate', e.target.value)}
                      required
                      className="h-11"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="preferredTimeToContact">Preferred Time *</Label>
                    <Select
                      value={formData.preferredTimeToContact}
                      onValueChange={(value) => handleInputChange('preferredTimeToContact', value)}
                      required
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select a time slot" />
                      </SelectTrigger>
                      <SelectContent>
                        {timeSlots.map((slot) => (
                          <SelectItem key={slot} value={slot}>
                            {slot}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Additional Notes */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">Additional Information</h3>
                <div className="space-y-2">
                  <Label htmlFor="note">Notes (Optional)</Label>
                  <Textarea
                    id="note"
                    placeholder="Tell us about your pharmacy, specific requirements, or any questions you have..."
                    value={formData.note}
                    onChange={(e) => handleInputChange('note', e.target.value)}
                    className="min-h-[100px] resize-none"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-green-600 hover:bg-green-700 text-white"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Submitting Request...
                  </div>
                ) : (
                  'Submit Demo Request'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <p className="text-xs text-gray-500">
            © 2025 Courier-RX. All rights reserved.
          </p>
        </div>
      </div>

      {/* Global loading overlay */}
      <GlobalLoader 
        isLoading={isLoading}
        message="Submitting demo request..."
        subMessage="Please wait while we process your request"
      />
    </div>
  );
}
